<template>
  <header class="moo-topbar" :class="variant ? 'moo-topbar--' + variant : null">
    <!-- Optional close (✕) on the left — used by MyCart to exit the cart.
         Absolutely positioned so it doesn't disturb the shared layout on
         Order/Category (which don't pass show-close). -->
    <button
      v-if="showClose"
      type="button"
      class="moo-topbar__close"
      aria-label="Close"
      @click="$emit('close')"
    >
      <i class="material-icons-round" aria-hidden="true">close</i>
    </button>

    <!-- Optional back (←) on the left — used by MyCart to return to the menu.
         Same absolute placement as the close button, so the brand stays
         centred on every screen. -->
    <button
      v-if="showBack"
      type="button"
      class="moo-topbar__back"
      aria-label="Back"
      @click="$emit('back')"
    >
      <i class="material-icons-round" aria-hidden="true">arrow_back</i>
    </button>

    <!-- Closed badge, carried over from the old OrderHeader: shown while the
         branch has stopped ordering. Takes the left slot, so it is skipped on
         screens that put a close/back button there. -->
    <img
      v-if="isStopOrdering && !showClose && !showBack"
      class="moo-topbar__closed"
      src="../assets/img/closed.png"
      alt="Closed"
    />

    <div v-if="variant !== 'compact'" class="moo-topbar__brand">
      <img
        v-if="brandLogoUrl"
        class="moo-topbar__brand-img"
        :src="brandLogoUrl"
        :alt="resolvedBrandName"
      />
      <span v-else class="moo-topbar__brand-text">{{ resolvedBrandName }}</span>
    </div>

    <!-- Language selector: brand pill + dropdown (Chn/Jap/Thai/Kor · Eng).
         UI-only selector for now — backend does not localise menu content yet.
         Left segment (label) toggles the shown language; right caret opens the
         pair dropdown — mirrors advocado's OrderTopBar interaction. -->
    <div class="moo-topbar__lang-wrap">
      <div class="moo-topbar__lang" role="group" aria-label="Language">
        <button
          type="button"
          class="moo-topbar__lang-seg"
          :style="{ background: themeColor }"
          :aria-label="`Switch language (currently ${activeLabel})`"
          @click="toggleLang"
        >
          <span class="moo-topbar__lang-text">{{ activeLabel }}</span>
        </button>
        <button
          type="button"
          class="moo-topbar__lang-caret"
          aria-haspopup="listbox"
          :aria-expanded="langOpen ? 'true' : 'false'"
          aria-label="Change language"
          @click="toggleLangMenu"
        >
          <i class="material-icons-round" aria-hidden="true">expand_more</i>
        </button>
      </div>

      <template v-if="langOpen">
        <div class="moo-topbar__lang-backdrop" @click="langOpen = false"></div>
        <ul class="moo-topbar__lang-menu" role="listbox">
          <li
            v-for="opt in langOptions"
            :key="opt.code"
            class="moo-topbar__lang-option"
            :class="{ 'is-active': opt.code === activeLang }"
            :style="opt.code === activeLang ? { background: themeColor } : null"
            role="option"
            :aria-selected="opt.code === activeLang ? 'true' : 'false'"
            @click="selectLang(opt)"
          >
            {{ opt.label }}
          </li>
        </ul>
      </template>
    </div>
  </header>
</template>

<script>
import { localStorageName } from "@/config/utils";
import {
  LANGUAGE_PAIRS,
  readLanguagePair,
  setLanguagePair,
  setLocale,
  toggleLocale,
} from "@/i18n";

// Bilingual pairs, defined once in @/i18n so the store and the pill can't drift.
const LANG_OPTIONS = LANGUAGE_PAIRS.map((p) => ({
  code: p.second,
  label: p.label,
}));
// Short single-language labels shown on the left toggle segment.
const LANG_LABEL = {
  en: "Eng",
  zh: "Chn",
  ja: "Jap",
  th: "Thai",
  ko: "Kor",
};
export default {
  name: "OrderTopBar",
  props: {
    // Optional override; when omitted the brand name is read from the API value
    // persisted by App.vue (MERCHANT_BRAND_NAME).
    brandName: {
      type: String,
      default: "",
    },
    // Show a close (✕) button on the left (MyCart uses it to exit the cart).
    showClose: {
      type: Boolean,
      default: false,
    },
    // Show a back (←) button on the left (MyCart uses it to return to the menu).
    showBack: {
      type: Boolean,
      default: false,
    },
    // M1045: "overlay" lays the bar over the menu banner (transparent, not
    // sticky); "compact" renders only the language pill for the slim bar that
    // replaces it once the banner scrolls away. Empty = the sticky bar.
    variant: {
      type: String,
      default: "",
    },
  },
  emits: ["lang", "close", "back"],
  data() {
    return {
      // Whether the language dropdown is open.
      langOpen: false,
      // The second (non-English) language of the active pair, persisted by @/i18n.
      activeLang: readLanguagePair(),
    };
  },
  computed: {
    langOptions() {
      return LANG_OPTIONS;
    },
    // The language currently displayed — always English or the pair's second
    // language. Read from vue-i18n so the pill follows the app-wide locale.
    activeCode() {
      return this.$i18n.locale;
    },
    activeLabel() {
      return LANG_LABEL[this.activeCode] || LANG_LABEL.en;
    },
    // BRANCH_INFO is written by App.vue before any route renders.
    isStopOrdering() {
      try {
        const branch = JSON.parse(this.readStore("BRANCH_INFO") || "null");
        return !!branch && Number(branch.stop_ordering) === 1;
      } catch (e) {
        return false;
      }
    },
    resolvedBrandName() {
      if (this.brandName) return this.brandName;
      return this.readStore("MERCHANT_BRAND_NAME") || "";
    },
    // Merchant's Theme 2 logo, falling back to the generic merchant logo.
    brandLogoUrl() {
      return this.$store.state.logoTheme2 || this.readStore("MERCHANT_LOGO") || "";
    },
    // Merchant's Theme 2 colour, used for the language (globe) icon.
    themeColor() {
      return (
        this.$store.state.mainThemeColorTheme2 ||
        this.$store.state.color ||
        "#201a1a"
      );
    },
  },
  methods: {
    readStore(key) {
      try {
        return localStorage.getItem(localStorageName(key)) || "";
      } catch (e) {
        return "";
      }
    },
    toggleLangMenu() {
      // Order mounts two bars (overlay + compact); re-read so the highlighted
      // pair follows a change made from the other one.
      this.activeLang = readLanguagePair();
      this.langOpen = !this.langOpen;
    },
    // Left segment: flip between English and the active pair's second language.
    toggleLang() {
      this.langOpen = false;
      this.$emit("lang", toggleLocale());
    },
    // Select a language pairing from the dropdown: persist the pair, display
    // its second language right away, and notify the parent so it can reload
    // menu-name translations.
    selectLang(opt) {
      this.activeLang = setLanguagePair(opt.code);
      this.langOpen = false;
      this.$emit("lang", setLocale(this.activeLang));
    },
  },
};
</script>

<style scoped lang="scss">
.moo-topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 74px;
  padding: 0 1.5rem;
  background: var(--theme-30, #ffffff);
}

/* M1045: sits inside the banner overlay (Order/Category). */
.moo-topbar--overlay {
  position: relative;
  z-index: auto;
  height: 44px;
  padding: 0;
  background: transparent;

  .moo-topbar__brand-img {
    max-height: 40px;
    max-width: 140px;
    border-radius: 10px;
  }

  .moo-topbar__brand-text {
    color: #ffffff;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
  }

  .moo-topbar__lang {
    height: 32px;
  }
}

/* M1045: language pill only, inside the slim sticky bar. */
.moo-topbar--compact {
  position: static;
  flex: none;
  height: auto;
  padding: 0;
  background: transparent;
}

.moo-topbar__brand {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  pointer-events: none;
}

.moo-topbar__brand-img {
  max-width: 168px;
  max-height: 60px;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 12px;
}

.moo-topbar__brand-text {
  font-size: 1.3rem;
  font-weight: 900;
  letter-spacing: -0.03em;
  color: #201a1a;
  white-space: nowrap;
}

.moo-topbar__lang-wrap {
  position: relative;
  display: inline-flex;
}

.moo-topbar__lang {
  display: inline-flex;
  align-items: stretch;
  height: 36px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.moo-topbar__lang-seg {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 0 0.75rem;
  border: none;
  background: #201a1a;
  color: #ffffff;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:active {
    filter: brightness(0.92);
  }
}

.moo-topbar__lang-text {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1;
}

.moo-topbar__lang-caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  padding: 0;
  border: none;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  background: transparent;
  color: #201a1a;
  cursor: pointer;

  &:active {
    transform: scale(0.94);
  }

  i {
    font-size: 20px;
    color: #201a1a;
  }
}

.moo-topbar__lang-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}

.moo-topbar__lang-menu {
  position: absolute;
  top: calc(100% + 7px);
  right: 0;
  z-index: 41;
  min-width: 132px;
  margin: 0;
  padding: 0.3rem;
  list-style: none;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.14);
}

.moo-topbar__lang-option {
  padding: 0.5rem 0.7rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #201a1a;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease;

  &:not(.is-active):active {
    background: rgba(0, 0, 0, 0.05);
  }
}

.moo-topbar__lang-option.is-active {
  color: #ffffff;
}

.moo-topbar__closed {
  position: absolute;
  left: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  pointer-events: none;
}

.moo-topbar__close,
.moo-topbar__back {
  position: absolute;
  left: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: #201a1a;
  cursor: pointer;
  transition: transform 0.2s ease;

  i {
    font-size: 28px;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
}
</style>
